/**
 * Smooth pursuit — the first real exercise (Phase 2).
 *
 * DESIGNED TO ABSORB FUTURE EXERCISES RATHER THAN MULTIPLY THEM
 * -------------------------------------------------------------
 * docs/ADDING_AN_EXERCISE.md: "Most 'new exercises' should turn out to be new
 * parameter values for an existing one." This module is built to make that
 * true for the whole pursuit family. Horizontal, vertical, diagonal, circular,
 * figure-of-eight and arbitrary custom geometry are ONE module with different
 * settings — not six modules.
 *
 * The path is a Lissajous curve:
 *
 *     x(θ) = A · sin(fx·θ + φ)
 *     y(θ) = B · sin(fy·θ)
 *
 * That single equation covers every geometry named above, and a great many
 * that have not been named yet:
 *
 *     horizontal     fx 1  fy 0  φ 0     a flat line
 *     vertical       fx 0  fy 1  φ 0     an upright line
 *     diagonal       fx 1  fy 1  φ 0     corner to corner, either sense
 *     circular       fx 1  fy 1  φ 90    a circle, or an ellipse off-ratio
 *     figure eight   fx 1  fy 2  φ 90    a horizontal 8
 *     custom         doctor sets fx, fy and φ directly
 *
 * The diagonals stretch to the canvas aspect ratio rather than sitting at a
 * fixed 45 degrees, so they run genuinely corner to corner on a wide screen.
 *
 * Raising `pathFreqY` to 3, 4 or 5 produces progressively more complex closed
 * figures with no code change whatsoever. That is the intended growth path for
 * this exercise: new clinical variants arrive as prescription parameters.
 *
 * CONSTANT TANGENTIAL SPEED
 * -------------------------
 * Speed is prescribed in degrees of visual angle per second, which is the
 * clinically meaningful quantity — smooth pursuit degrades into catch-up
 * saccades above roughly 30–40 deg/s, and that threshold is much of what makes
 * this exercise diagnostic.
 *
 * Advancing the Lissajous parameter θ at a constant rate would NOT produce a
 * constant speed: the target would race through the straights and crawl round
 * the curves, so "20 deg/s" would mean nothing. The module therefore builds an
 * arc-length lookup table once at start and advances by DISTANCE each frame,
 * so the prescribed speed is the speed the patient's eyes actually track, on
 * every geometry.
 *
 * HOW IT IS MEASURED
 * ------------------
 * THE PATIENT DOES NOTHING WITH THEIR HANDS. They watch the dot.
 *
 * WHY THERE IS NO RESPONSE — changed 2026-08-23, and this is the whole story
 * of how this exercise is measured.
 *
 * Webcam eye-tracking is rejected (DECISIONS.md), so the app cannot see the
 * eyes. From Phase 2 until now the patient followed the target with the mouse
 * or a fingertip, and the error between cursor and target was sampled every
 * frame and emitted as `tracking_error_deg` — a logged tradeoff that measured
 * hand-eye pursuit rather than ocular pursuit.
 *
 * That is gone. The objection that ended it was not about the number being
 * impure; it was that asking for the hand CHANGES WHAT THE EYES DO:
 *
 *   Holding a cursor on a moving dot puts the patient's attention on their
 *   hand. On a touchscreen the finger physically covers the dot they are meant
 *   to be following. A patient watching their own fingertip is not performing
 *   smooth pursuit at all — so collecting the measurement degraded the therapy
 *   it was measuring. In tandem stance, which is a prescribed progression, it
 *   is worse again.
 *
 * The exercise is therefore unverifiable, and that is accepted: the platform
 * trains and tracks, it does not assess (§11). We could never see pursuit
 * QUALITY anyway — a patient lagging, or catching up with a string of small
 * saccades, looked identical to clean tracking. What carries the record now is
 * compliance and the symptom rating the patient already taps between exercises.
 *
 * The stimulus still does the work: a patient looking at the screen tracks a
 * moving dot by reflex, so doing it imperfectly is still doing it.
 *
 * Full reasoning in DECISIONS.md 2026-08-23.
 */
import { degToPx } from '../types.js';
/**
 * Preset geometries. `custom` is absent deliberately — it reads the doctor's
 * own freq/phase parameters instead.
 */
const PATH_PRESETS = {
    horizontal: { freqX: 1, freqY: 0, phaseDeg: 0 },
    vertical: { freqX: 0, freqY: 1, phaseDeg: 0 },
    diagonal_up: { freqX: 1, freqY: 1, phaseDeg: 0, fitAspect: true, mirrorOnReverse: true },
    diagonal_down: {
        freqX: 1,
        freqY: 1,
        phaseDeg: 0,
        fitAspect: true,
        invertY: true,
        mirrorOnReverse: true,
    },
    circular: { freqX: 1, freqY: 1, phaseDeg: 90 },
    figure_eight: { freqX: 1, freqY: 2, phaseDeg: 90 },
};
// =============================================================================
// Clinical colour choices
//
// Fixed palettes rather than a free colour picker. The doctor is choosing a
// stimulus, not designing a page, and an open picker invites values that are
// simply wrong for a photophobic patient. These live here rather than in
// tokens.css deliberately: target and background colour are per-patient
// CLINICAL parameters, not theme (docs/ARCHITECTURE.md, §3).
// =============================================================================
const TARGET_COLOURS = [
    { value: '#f0f0f0', label: 'White' },
    { value: '#e02020', label: 'Red' },
    { value: '#2f6fe0', label: 'Blue' },
    { value: '#24a148', label: 'Green' },
    { value: '#f07800', label: 'Orange' },
    { value: '#8a3ffc', label: 'Purple' },
    { value: '#000000', label: 'Black' },
];
/**
 * Crosshair colour for each target colour — the opposing hue, so the fixation
 * cross reads clearly against the disc it sits inside. White takes red, red
 * takes white, and the rest take their colour-wheel opposite.
 */
const CROSSHAIR_COMPLEMENT = {
    '#f0f0f0': '#e02020', // white  -> red
    '#e02020': '#f0f0f0', // red    -> white
    '#2f6fe0': '#f0b000', // blue   -> amber
    '#24a148': '#e0208a', // green  -> magenta
    '#f07800': '#2f6fe0', // orange -> blue
    '#8a3ffc': '#c8e020', // purple -> yellow-green
    '#000000': '#f0f0f0', // black  -> white
};
const BACKGROUND_COLOURS = [
    { value: '#14161a', label: 'Dark (low stimulus)' },
    { value: '#000000', label: 'Black' },
    { value: '#808080', label: 'Grey' },
    { value: '#ffffff', label: 'White' },
    { value: '#b00020', label: 'Red' },
    { value: '#1a3f8f', label: 'Blue' },
    { value: '#e8c400', label: 'Yellow' },
    { value: '#5b2a86', label: 'Purple' },
];
function shapeFor(params) {
    const preset = PATH_PRESETS[String(params.path)];
    if (preset)
        return preset;
    return {
        freqX: Number(params.pathFreqX),
        freqY: Number(params.pathFreqY),
        phaseDeg: Number(params.pathPhaseDeg),
    };
}
/**
 * The largest amplitude that still keeps the whole target inside the canvas,
 * in degrees of visual angle.
 *
 * Without this, a prescription written for a large screen renders on a smaller
 * one with the target sailing off the edge and sitting outside the viewport for
 * seconds at a time — the patient has nothing to pursue, and the tracking error
 * recorded during those seconds is meaningless. The exercise fits the amplitude
 * to the screen it is actually on and reports what it used.
 */
function fitAmplitude(requestedDeg, halfExtentPx, targetRadiusPx, pxPerDegree) {
    const usablePx = Math.max(halfExtentPx - targetRadiusPx, 0);
    return Math.min(requestedDeg, usablePx / pxPerDegree);
}
/** Position on the path at parameter θ, in degrees of visual angle from centre. */
function pointAt(theta, shape, amplitudeDeg, verticalAmplitudeDeg) {
    const phase = (shape.phaseDeg * Math.PI) / 180;
    return {
        x: amplitudeDeg * Math.sin(shape.freqX * theta + phase),
        y: verticalAmplitudeDeg * Math.sin(shape.freqY * theta),
    };
}
const ARC_SAMPLES = 2048;
function buildArcTable(shape, amplitudeDeg, verticalAmplitudeDeg, thetaStart, thetaEnd) {
    const cumulative = new Float64Array(ARC_SAMPLES + 1);
    const theta = new Float64Array(ARC_SAMPLES + 1);
    const span = thetaEnd - thetaStart;
    let previous = pointAt(thetaStart, shape, amplitudeDeg, verticalAmplitudeDeg);
    let distance = 0;
    theta[0] = thetaStart;
    cumulative[0] = 0;
    for (let i = 1; i <= ARC_SAMPLES; i += 1) {
        const t = thetaStart + (i / ARC_SAMPLES) * span;
        const point = pointAt(t, shape, amplitudeDeg, verticalAmplitudeDeg);
        distance += Math.hypot(point.x - previous.x, point.y - previous.y);
        theta[i] = t;
        cumulative[i] = distance;
        previous = point;
    }
    return { cumulative, theta, total: distance };
}
/**
 * Is this path a straight line rather than a genuine loop?
 *
 * It matters because the two kinds need completely different treatment for
 * one-way travel. A closed loop — a circle, a figure of eight — is already
 * one-way when the curve parameter advances steadily; the target simply keeps
 * going round. A straight line is not: the Lissajous equation traces a line by
 * running out to one end and back again, so advancing the parameter steadily
 * produces oscillation no matter what, which is exactly the "continuous is
 * still going back and forth" problem.
 *
 * For a line, one-way has to mean sweep to the far end, reset, sweep again —
 * so the module traverses only the outward half of the cycle and wraps.
 *
 * Detected rather than hard-coded, because a Custom path may be either. A line
 * is symmetric about θ = π/2: the point at θ and the point at π − θ coincide.
 * A circle or figure of eight does not have that property.
 */
function isStraightLine(shape, amplitudeDeg, verticalAmplitudeDeg) {
    const scale = Math.max(Math.abs(amplitudeDeg), Math.abs(verticalAmplitudeDeg), 1e-6);
    for (let i = 1; i <= 8; i += 1) {
        const t = (i / 9) * Math.PI;
        const a = pointAt(t, shape, amplitudeDeg, verticalAmplitudeDeg);
        const b = pointAt(Math.PI - t, shape, amplitudeDeg, verticalAmplitudeDeg);
        if (Math.hypot(a.x - b.x, a.y - b.y) > scale * 1e-6)
            return false;
    }
    return true;
}
/** Invert the arc-length table: distance travelled to θ. Binary search. */
function thetaAtDistance(table, distance) {
    if (table.total <= 0)
        return 0;
    // Wrap into one cycle. Negative distances wrap too, so reversals work.
    let target = distance % table.total;
    if (target < 0)
        target += table.total;
    let low = 0;
    let high = ARC_SAMPLES;
    while (low + 1 < high) {
        const mid = (low + high) >> 1;
        if (table.cumulative[mid] <= target)
            low = mid;
        else
            high = mid;
    }
    const spanStart = table.cumulative[low];
    const spanEnd = table.cumulative[high];
    const span = spanEnd - spanStart;
    const fraction = span > 0 ? (target - spanStart) / span : 0;
    return table.theta[low] + fraction * (table.theta[high] - table.theta[low]);
}
// =============================================================================
// Sampling
// =============================================================================
/** Reversal interval bounds for unpredictable mode, in seconds. */
const REVERSAL_MIN_SEC = 1.2;
const REVERSAL_MAX_SEC = 4;
// =============================================================================
// The module
// =============================================================================
export const smoothPursuit = {
    definition: {
        id: 'smooth_pursuit',
        // Bumped to 2 on 2026-08-23 when the pointer was removed. Results recorded
        // under version 1 carry tracking error; from version 2 there is only
        // completion. The tag is what stops a chart showing that as a patient
        // falling off a cliff.
        version: 2,
        name: 'Smooth pursuit',
        category: 'oculomotor',
        clinicalDescription: 'Continuous tracking of a target moving at constant angular velocity along a ' +
            'configurable path. Horizontal, vertical, diagonal, circular and figure-of-eight ' +
            'geometries are parameter settings of this one exercise, as is arbitrary custom ' +
            'geometry. Unpredictable mode inserts random direction reversals, removing ' +
            'anticipation and forcing re-acquisition.',
        patientInstruction: 'Follow the moving dot with your eyes, staying on it as closely as you can. ' +
            'Keep your head still and let only your eyes move. There is nothing to click. ' +
            'If symptoms start to build, press the spacebar to stop — stopping early is ' +
            'the right call.',
        params: {
            path: {
                kind: 'enum',
                label: 'Path',
                help: 'The shape the target traces. Choose Custom to set the geometry directly.',
                options: [
                    { value: 'horizontal', label: 'Horizontal' },
                    { value: 'vertical', label: 'Vertical' },
                    { value: 'diagonal_up', label: 'Diagonal — rising' },
                    { value: 'diagonal_down', label: 'Diagonal — falling' },
                    { value: 'circular', label: 'Circular' },
                    { value: 'figure_eight', label: 'Figure of eight' },
                    { value: 'custom', label: 'Custom' },
                ],
                default: 'horizontal',
            },
            travel: {
                kind: 'enum',
                label: 'Travel',
                help: 'One way sends the target across in a single direction and starts again — ' +
                    'on a loop it keeps going round without turning back. Back and forth ' +
                    'reverses at each end of the path.',
                options: [
                    { value: 'one_way', label: 'One way' },
                    { value: 'back_and_forth', label: 'Back and forth' },
                ],
                default: 'back_and_forth',
            },
            travelDirection: {
                kind: 'enum',
                label: 'Which way',
                help: 'On a vertical path this reads as upward or downward. Ignored while ' +
                    'Unpredictable is on, since that reverses the target at random.',
                options: [
                    { value: 'forward', label: 'Rightward / clockwise' },
                    { value: 'reverse', label: 'Leftward / anticlockwise' },
                ],
                default: 'forward',
            },
            amplitudeDeg: {
                kind: 'number',
                label: 'Amplitude',
                help: 'Maximum distance from centre, in degrees of visual angle. The target ' +
                    'sweeps twice this edge to edge.',
                min: 2,
                max: 25,
                step: 1,
                default: 13,
                unit: 'deg',
                fromCentre: true,
            },
            speedDegPerSec: {
                kind: 'number',
                label: 'Speed',
                help: 'Constant target velocity along the path. Smooth pursuit typically breaks ' +
                    'down into catch-up saccades above roughly 30–40 deg/s.',
                min: 2,
                max: 60,
                step: 1,
                default: 10,
                unit: 'deg/s',
            },
            durationSec: {
                kind: 'number',
                label: 'Duration',
                min: 30,
                max: 600,
                step: 30,
                default: 60,
                unit: 's',
                displayUnit: { unit: 'min', factor: 60 },
            },
            targetSizeDeg: {
                kind: 'number',
                label: 'Target size',
                help: 'Diameter in degrees of visual angle. A smaller target demands more precise ' +
                    'foveation; a larger one is more tolerable for symptomatic patients.',
                min: 0.5,
                max: 4,
                step: 0.5,
                default: 1,
                unit: 'deg',
            },
            targetColor: {
                kind: 'enum',
                label: 'Target colour',
                help: 'A clinical parameter, not a branding choice. Lower contrast against the ' +
                    'background for photophobic patients.',
                options: TARGET_COLOURS,
                default: '#f0f0f0',
            },
            crosshair: {
                kind: 'boolean',
                label: 'Crosshair on target',
                help: 'Draws fine lines through the target to sharpen the fixation point. Useful ' +
                    'when a plain disc is too diffuse to foveate precisely.',
                default: true,
            },
            backgroundColor: {
                kind: 'enum',
                label: 'Background',
                help: 'The exercise canvas. Darker surfaces are lower stimulus for ' +
                    'photophobic patients; white is the brightest.',
                options: BACKGROUND_COLOURS,
                default: '#808080',
            },
            backgroundTexture: {
                kind: 'boolean',
                label: 'Textured background',
                help: 'Adds a faint depth texture behind the target. Gives the eye static ' +
                    'reference detail to move against instead of an empty field.',
                default: true,
            },
            unpredictable: {
                kind: 'boolean',
                label: 'Unpredictable',
                help: 'Inserts random direction reversals so the patient cannot anticipate the ' +
                    'path. Substantially harder — it converts steady pursuit into repeated ' +
                    're-acquisition.',
                default: false,
            },
            // --- Advanced: custom geometry ---------------------------------------
            // Read only when Path is set to Custom. Presets supply their own values.
            verticalAmplitudePct: {
                kind: 'number',
                label: 'Vertical amplitude',
                help: 'Vertical extent as a percentage of the horizontal amplitude. 100 gives a ' +
                    'true circle; lower values flatten it into an ellipse.',
                min: 0,
                max: 200,
                step: 5,
                default: 100,
                unit: 'pct',
                advanced: true,
            },
            pathFreqX: {
                kind: 'number',
                label: 'Custom — horizontal frequency',
                help: 'Lissajous fx. Only used when Path is Custom.',
                min: 0,
                max: 8,
                step: 1,
                default: 1,
                unit: 'count',
                advanced: true,
            },
            pathFreqY: {
                kind: 'number',
                label: 'Custom — vertical frequency',
                help: 'Lissajous fy. Only used when Path is Custom. Raising this against the ' +
                    'horizontal frequency produces progressively more complex closed figures.',
                min: 0,
                max: 8,
                step: 1,
                default: 2,
                unit: 'count',
                advanced: true,
            },
            pathPhaseDeg: {
                kind: 'number',
                label: 'Custom — phase offset',
                help: 'Lissajous φ, in degrees. Only used when Path is Custom. At equal ' +
                    'frequencies, 0 gives a diagonal line and 90 gives a circle.',
                min: 0,
                max: 360,
                step: 15,
                default: 90,
                unit: 'deg',
                advanced: true,
            },
        },
        /**
         * Completion only. With no patient response there is nothing else this
         * exercise can honestly report — see the header.
         */
        metrics: ['completion_pct'],
        decay: [],
        decayNotMonitored: 'Takes no patient response, so it emits no performance metric for the ' +
            'monitor to read. Following the dot with a cursor put the patient’s ' +
            'attention on their hand and changed what their eyes were doing, which ' +
            'is the therapy. Symptoms and compliance carry the record. Changed ' +
            '2026-08-23.',
        allowedPositions: ['seated', 'standing', 'tandem'],
        /** Empty: the patient watches. The only control is the host's stop. */
        allowedInputModes: [],
        involvesHeadMovement: false,
        involvesEyesClosed: false,
        /** The parameter the safety system shortens when reducing a dose. */
        durationParam: 'durationSec',
        estimateDurationSec(params) {
            return Math.round(Number(params.durationSec) + 10); // + instruction screen
        },
        /**
         * The traced shape, for the static drawing beside the settings.
         *
         * Built from the SAME `shapeFor()` and `pointAt()` the run loop uses, so
         * the drawing and the movement cannot describe different figures. What it
         * deliberately leaves out is the run loop's clamping to the canvas and the
         * reverse-direction mirroring: neither changes the figure, only where it
         * sits and which way it is travelled.
         */
        pathOutline(params, aspect) {
            const shape = shapeFor(params);
            // Corner-to-corner diagonals are defined against the shape of the area
            // they run in; everything else uses the doctor's vertical amplitude.
            const verticalScale = shape.fitAspect
                ? aspect
                : Number(params.verticalAmplitudePct) / 100;
            const signedVertical = verticalScale * (shape.invertY ? -1 : 1);
            const SAMPLES = 240;
            const points = [];
            for (let i = 0; i <= SAMPLES; i += 1) {
                const theta = (i / SAMPLES) * Math.PI * 2;
                points.push(pointAt(theta, shape, 1, signedVertical));
            }
            // A path that does not MOVE has no shape to draw. Both frequencies at
            // zero pins the target at x = sin(phase), which is a stationary dot
            // somewhere off-centre rather than at the origin — so this has to test
            // the spread of the points, not their distance from the middle. Getting
            // that wrong draws 241 copies of one point and shows an empty box.
            let minX = Infinity;
            let maxX = -Infinity;
            let minY = Infinity;
            let maxY = -Infinity;
            for (const point of points) {
                minX = Math.min(minX, point.x);
                maxX = Math.max(maxX, point.x);
                minY = Math.min(minY, point.y);
                maxY = Math.max(maxY, point.y);
            }
            const STILL = 1e-9;
            if (maxX - minX < STILL && maxY - minY < STILL)
                return [];
            // Normalise to -1..1 by the LARGER axis, so proportions survive: a
            // flattened ellipse stays flattened rather than being stretched round.
            const extent = Math.max(Math.abs(minX), maxX, Math.abs(minY), maxY);
            if (extent <= 0)
                return [];
            return points.map((point) => ({ x: point.x / extent, y: point.y / extent }));
        },
        validate(params) {
            const messages = [];
            const amplitude = Number(params.amplitudeDeg);
            const targetSize = Number(params.targetSizeDeg);
            // Blocking: the target never meaningfully leaves where it started, so
            // there is no pursuit left to measure.
            if (targetSize * 2 >= amplitude) {
                messages.push({
                    severity: 'blocking',
                    param: 'targetSizeDeg',
                    message: 'Target size is too large for this amplitude — the target would barely ' +
                        'leave its own starting position. Increase amplitude or reduce target size.',
                });
            }
            if (String(params.path) === 'custom') {
                const fx = Number(params.pathFreqX);
                const fy = Number(params.pathFreqY);
                if (fx === 0 && fy === 0) {
                    messages.push({
                        severity: 'blocking',
                        param: 'pathFreqX',
                        message: 'Custom path with both frequencies at zero would not move.',
                    });
                }
            }
            if (String(params.path) === 'vertical' &&
                Number(params.verticalAmplitudePct) === 0) {
                messages.push({
                    severity: 'blocking',
                    param: 'verticalAmplitudePct',
                    message: 'A vertical path with zero vertical amplitude would not move.',
                });
            }
            return messages;
        },
    },
    // ===========================================================================
    // The run half — browser only. Nothing above this line touches the DOM.
    // ===========================================================================
    run(ctx) {
        const { params, canvas } = ctx;
        const requestedAmplitudeDeg = Number(params.amplitudeDeg);
        const speedDegPerSec = Number(params.speedDegPerSec);
        const durationSec = Number(params.durationSec);
        const targetSizeDeg = Number(params.targetSizeDeg);
        const targetColor = String(params.targetColor);
        const backgroundColor = String(params.backgroundColor);
        const unpredictable = params.unpredictable === true;
        const reversing = String(params.travel) === 'back_and_forth';
        const crosshair = params.crosshair === true;
        const crosshairColor = CROSSHAIR_COMPLEMENT[targetColor] ?? '#e02020';
        const textured = params.backgroundTexture === true;
        const shape = shapeFor(params);
        const surface = canvas.getContext('2d');
        // Geometry depends on the size of the screen this is actually running on,
        // so it is resolved in start() rather than at module scope.
        let amplitudeDeg = requestedAmplitudeDeg;
        let verticalAmplitudeDeg = requestedAmplitudeDeg;
        let arc = { cumulative: new Float64Array(1), theta: new Float64Array(1), total: 0 };
        let texture = null;
        /**
         * Which way "forward" runs, resolved per path type so the label means what
         * it says. A straight line sweeps rightward; a loop turns clockwise on
         * screen, which is the curve parameter running backwards.
         */
        let directionSign = 1;
        /**
         * Canvas geometry, cached.
         *
         * `getBoundingClientRect()` forces the browser to recompute layout. Calling
         * it inside the draw loop meant doing that sixty times a second — enough to
         * cost frames and make the target judder, which reads as blur on a moving
         * object. It is now measured once and refreshed only when the canvas
         * actually changes size.
         */
        let box = { width: 0, height: 0 };
        let devicePixelScale = 1;
        let resizeObserver = null;
        function measure() {
            const rect = canvas.getBoundingClientRect();
            box = { width: rect.width, height: rect.height };
            devicePixelScale = rect.width > 0 ? canvas.width / rect.width : 1;
        }
        /**
         * Resolve amplitudes against the real canvas, then build the arc table.
         *
         * Two things happen here that cannot happen in the pure definition:
         * the diagonal presets stretch to the canvas aspect ratio so they run
         * genuinely corner to corner rather than at a fixed 45 degrees, and every
         * path is clamped so the target cannot leave the screen.
         */
        function resolveGeometry() {
            measure();
            const rect = box;
            const targetRadiusPx = degToPx(targetSizeDeg, ctx.pxPerDegree) / 2;
            const aspect = rect.width > 0 ? rect.height / rect.width : 1;
            const verticalScale = shape.fitAspect
                ? aspect
                : Number(params.verticalAmplitudePct) / 100;
            // How far the target ACTUALLY travels on each axis for this path. A
            // horizontal path never leaves y=0 however tall its nominal amplitude,
            // and a vertical path never leaves x=0 — so neither should be squeezed
            // by a limit on an axis it does not use.
            const phase = (shape.phaseDeg * Math.PI) / 180;
            const extentX = requestedAmplitudeDeg * (shape.freqX === 0 ? Math.abs(Math.sin(phase)) : 1);
            const extentY = shape.freqY === 0 ? 0 : requestedAmplitudeDeg * verticalScale;
            const scaleFor = (extent, halfPx) => extent > 0
                ? fitAmplitude(extent, halfPx, targetRadiusPx, ctx.pxPerDegree) / extent
                : 1;
            // Shrink both axes by the tighter constraint, so fitting to the screen
            // never silently distorts the prescribed shape into a different one.
            const scale = Math.min(scaleFor(extentX, rect.width / 2), scaleFor(extentY, rect.height / 2));
            const forward = String(params.travelDirection) !== 'reverse';
            // Keep "rising" rising and "falling" falling when the target runs the
            // other way, by mirroring the line as well as reversing the traversal.
            const invertY = shape.mirrorOnReverse
                ? Boolean(shape.invertY) !== !forward
                : Boolean(shape.invertY);
            amplitudeDeg = requestedAmplitudeDeg * scale;
            verticalAmplitudeDeg =
                requestedAmplitudeDeg * verticalScale * scale * (invertY ? -1 : 1);
            // A straight line is traversed over the outward half of the cycle only,
            // starting at the far negative end so that "forward" sweeps rightward.
            // A loop is traversed over the whole cycle, and "forward" is made to run
            // clockwise on screen by advancing the parameter backwards.
            const straight = isStraightLine(shape, amplitudeDeg, verticalAmplitudeDeg);
            const [thetaStart, thetaEnd] = straight
                ? [Math.PI * 1.5, Math.PI * 2.5]
                : [0, Math.PI * 2];
            const baseSign = straight ? 1 : -1;
            directionSign = (forward ? baseSign : -baseSign);
            arc = buildArcTable(shape, amplitudeDeg, verticalAmplitudeDeg, thetaStart, thetaEnd);
            // What this screen can actually show, which is not always what was
            // prescribed. Reported on every resolve, including after a resize, so the
            // record describes the run rather than the intention.
            ctx.reportDelivered({ amplitudeDeg: Math.round(amplitudeDeg * 100) / 100 });
        }
        /**
         * A faint static texture, so the eye has stationary detail to track the
         * target against rather than an empty field. Built once, into an offscreen
         * tile, and repeated — cheap enough to redraw every frame.
         */
        function buildTexture() {
            if (!surface || !textured)
                return null;
            const tile = document.createElement('canvas');
            tile.width = 64;
            tile.height = 64;
            const tileSurface = tile.getContext('2d');
            if (!tileSurface)
                return null;
            tileSurface.fillStyle = backgroundColor;
            tileSurface.fillRect(0, 0, 64, 64);
            // Low-contrast speckle. Deterministic spacing, jittered position, so it
            // reads as texture rather than as a grid the patient could fixate on.
            tileSurface.fillStyle = 'rgba(128,128,128,0.14)';
            for (let x = 0; x < 64; x += 8) {
                for (let y = 0; y < 64; y += 8) {
                    const jitterX = ((x * 7 + y * 13) % 5) - 2;
                    const jitterY = ((x * 11 + y * 5) % 5) - 2;
                    tileSurface.beginPath();
                    tileSurface.arc(x + jitterX, y + jitterY, 1.2, 0, Math.PI * 2);
                    tileSurface.fill();
                }
            }
            return surface.createPattern(tile, 'repeat');
        }
        let frame = 0;
        let stopped = false;
        let startedAt = 0;
        let lastFrameAt = 0;
        /**
         * Seconds this exercise has actually been RUNNING, accumulated from clamped
         * frame deltas rather than read off the wall clock.
         *
         * This distinction matters clinically. Browsers pause animation frames
         * whenever the tab is hidden — minimised, switched away from, screen
         * locked. Wall-clock elapsed time keeps counting through that; the target
         * does not move. Using wall clock, a patient who tabbed away for thirty
         * seconds would come back to an exercise that believed it was thirty
         * seconds further along, would end early, and would record a completion
         * percentage it had not earned. Accumulating deltas instead means the
         * exercise measures the time it genuinely ran, and simply pauses while
         * hidden.
         */
        let runningSec = 0;
        /** Signed distance travelled along the path, in degrees. */
        let travelled = 0;
        let direction = 1;
        let nextReversalAt = 0;
        function scheduleReversal(elapsedSec) {
            nextReversalAt =
                elapsedSec + REVERSAL_MIN_SEC + Math.random() * (REVERSAL_MAX_SEC - REVERSAL_MIN_SEC);
        }
        function draw() {
            if (stopped || !surface)
                return;
            const nowMs = ctx.now();
            const deltaSec = Math.min((nowMs - lastFrameAt) / 1000, 0.1); // clamp hidden-tab gaps
            lastFrameAt = nowMs;
            runningSec += deltaSec;
            const elapsedSec = runningSec;
            // --- advance along the path ------------------------------------------
            if (unpredictable && elapsedSec >= nextReversalAt) {
                direction = direction === 1 ? -1 : 1;
                scheduleReversal(elapsedSec);
            }
            travelled += directionSign * direction * speedDegPerSec * deltaSec;
            // "Back and forth" folds the distance so the second half of each lap
            // retraces the first — on a circle, clockwise then anticlockwise.
            let pathDistance = travelled;
            if (reversing && arc.total > 0) {
                const lap = ((travelled % (arc.total * 2)) + arc.total * 2) % (arc.total * 2);
                pathDistance = lap <= arc.total ? lap : arc.total * 2 - lap;
            }
            const theta = thetaAtDistance(arc, pathDistance);
            const point = pointAt(theta, shape, amplitudeDeg, verticalAmplitudeDeg);
            // --- render ----------------------------------------------------------
            // Work in CSS pixels regardless of how the host sized the backing store,
            // so pxPerDegree from calibration means the same thing here. Both the box
            // and the scale are cached — see `measure()`.
            surface.setTransform(devicePixelScale, 0, 0, devicePixelScale, 0, 0);
            const width = box.width;
            const height = box.height;
            const centreX = width / 2;
            const centreY = height / 2;
            surface.fillStyle = backgroundColor;
            surface.fillRect(0, 0, width, height);
            if (texture) {
                surface.fillStyle = texture;
                surface.fillRect(0, 0, width, height);
            }
            const targetX = centreX + degToPx(point.x, ctx.pxPerDegree);
            const targetY = centreY - degToPx(point.y, ctx.pxPerDegree); // screen y is inverted
            const radiusPx = Math.max(degToPx(targetSizeDeg, ctx.pxPerDegree) / 2, 1);
            surface.beginPath();
            surface.arc(targetX, targetY, radiusPx, 0, Math.PI * 2);
            surface.fillStyle = targetColor;
            surface.fill();
            if (crosshair) {
                // Contained entirely within the disc, in the opposing colour, so it
                // sharpens the fixation point without enlarging the target or adding
                // anything for the eye to chase beyond its edge.
                surface.save();
                surface.beginPath();
                surface.arc(targetX, targetY, radiusPx, 0, Math.PI * 2);
                surface.clip();
                surface.strokeStyle = crosshairColor;
                surface.lineWidth = Math.max(radiusPx / 5, 1);
                surface.beginPath();
                surface.moveTo(targetX - radiusPx, targetY);
                surface.lineTo(targetX + radiusPx, targetY);
                surface.moveTo(targetX, targetY - radiusPx);
                surface.lineTo(targetX, targetY + radiusPx);
                surface.stroke();
                surface.restore();
            }
            // --- finish ----------------------------------------------------------
            if (elapsedSec >= durationSec) {
                ctx.emit({ t: elapsedSec * 1000, metric: 'completion_pct', value: 100 });
                ctx.finish('completed');
                return;
            }
            frame = requestAnimationFrame(draw);
        }
        return {
            start() {
                resolveGeometry();
                texture = buildTexture();
                startedAt = ctx.now();
                lastFrameAt = startedAt;
                scheduleReversal(0);
                // Keep the cached box honest if the canvas is resized under us —
                // entering full screen, or the window being dragged to another display.
                if (typeof ResizeObserver !== 'undefined') {
                    resizeObserver = new ResizeObserver(() => measure());
                    resizeObserver.observe(canvas);
                }
                frame = requestAnimationFrame(draw);
            },
            /**
             * Instant and idempotent (§8, rule 5). No confirmation, no fade-out.
             * May be called twice.
             */
            stop(reason) {
                if (stopped)
                    return;
                stopped = true;
                cancelAnimationFrame(frame);
                resizeObserver?.disconnect();
                resizeObserver = null;
                if (reason !== 'completed') {
                    ctx.emit({
                        t: runningSec * 1000,
                        metric: 'completion_pct',
                        value: Math.min(100, (runningSec / durationSec) * 100),
                    });
                }
            },
        };
    },
};
//# sourceMappingURL=smooth-pursuit.js.map